import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { createTeam, clearSuccessMessage } from "../../../redux/Slice/Settings/addfunctionsSlice";
import { fetchTeams } from "../../../redux/Slice/Settings/settingsSlice";
import { isSubscriptionBlockedError, checkSubscriptionActive } from "../../../utils/subscriptionError";
import { getAuthToken } from "../../../utils/getAuthToken";
import { toast } from "react-hot-toast";

const AddTeamsModal = ({ open, onClose }) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    teamName: "",
    description: "",
  });

  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const { loading, error, successMessage } = useSelector((state) => state.addFunctions);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validate = () => {
    const tempErrors = {};
    if (!formData.teamName) tempErrors.teamName = "Team Name is required.";
    if (!formData.description)
      tempErrors.description = "Description is required.";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const { blocked, message } = await checkSubscriptionActive(getAuthToken);
    if (blocked) { toast.error(message, { id: 'subscription-blocked', duration: 5000 }); return; }
    dispatch(createTeam(formData));
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Clear success message and reset form on modal close
  useEffect(() => {
    if (!open) return;
    setFormData({
      teamName: "",
      description: "",
    });
    setErrors({});
    dispatch(clearSuccessMessage());
  }, [open, dispatch]);

  // Close form on success — success toast is shown by the parent Teams component
  useEffect(() => {
    if (successMessage) {
      dispatch(fetchTeams());
      setFormData({ teamName: "", description: "" });
      setErrors({});
      onClose();
    }
  }, [successMessage, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!error) return;
    if (isSubscriptionBlockedError(error)) {
      // fetch-based thunk bypasses Axios interceptor — fire toast manually and close dialog
      toast.error(error, { id: 'subscription-blocked', duration: 5000 });
      onClose();
    } else {
      setSnackbar({ open: true, message: error, severity: "error" });
    }
  }, [error]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleModalClose = () => {
    setFormData({
      teamName: "",
      description: "",
    });
    setErrors({});
    dispatch(clearSuccessMessage());
    onClose();
  };

  return (
    <>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: "bold",
          fontSize: "24px",
          padding: "14px",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <Typography variant="h6">Create New Team</Typography>
        <IconButton onClick={handleModalClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "24px",
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            padding: 1,
            marginTop: { xs: "16px", sm: "20px", md: "24px", lg: "24px", xl: "24px" },
          }}
        >
          <TextField
            label="Team Name"
            name="teamName"
            variant="outlined"
            fullWidth
            value={formData.teamName}
            onChange={handleChange}
            error={!!errors.teamName}
            helperText={errors.teamName}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
          />
          <TextField
            label="Description"
            name="description"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={formData.description}
            onChange={handleChange}
            error={!!errors.description}
            helperText={errors.description}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
          />
        </Box>
      </DialogContent>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          padding: "16px 24px",
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <Box
          component="button"
          onClick={handleModalClose}
          sx={{
            borderRadius: "20px",
            textTransform: "capitalize",
            border: (theme) => `1px solid ${theme.palette.divider}`,
            color: (theme) => theme.palette.text.primary,
            backgroundColor: "transparent",
            padding: "8px 16px",
            cursor: "pointer",
            "&:hover": {
              borderColor: (theme) => theme.palette.primary.main,
            },
          }}
        >
          Cancel
        </Box>
        <Box
          component="button"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            borderRadius: "20px",
            textTransform: "capitalize",
            backgroundColor: (theme) => theme.palette.primary.main,
            color: "white",
            border: "none",
            padding: "8px 16px",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: (theme) => theme.palette.primary.dark,
            },
            "&:disabled": {
              backgroundColor: (theme) => theme.palette.action.disabled,
              cursor: "not-allowed",
            },
          }}
        >
          {loading ? "Creating..." : "Create"}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddTeamsModal;
